import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../../components/custom-table/custom-table";
import StatFileUploadModal from "../../../components/modals/StatFileUpload/statFileUploadModal";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { PaginationKeys } from "../../../enums/nycbsPharmaOrder";
import { useDrugCohort } from "../../../hooks/useDrugCohort";
import { useDrugColorMatcher } from "../../../hooks/useDrugColorMatcher";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { buildFileUploadButtonMeta } from "../../pharmaStpFile/utils/buttonDropdownConfig";

import CmmOrderExpandableRow from "../../../components/custom-table/expandableRowChild/CmmOrderExpandableRow/cmmOrderExpandableRow";
import CmmInputViewerModalForCmmOrders from "../../../components/modals/cmmInputViewerModal/cmmInputViewerModalForCmmOrders";
import CmmOrderDeleteModal from "../../../components/modals/cmmOrderDeleteModal/cmmOrderDeleteModal";
import DrugLabelModal from "../../../components/modals/drugLabelModal/drugLabelModal";
import ReportMedicationModal from "../../../components/modals/reportMedication/reportMedication";
import ReportPrescriptionModal from "../../../components/modals/reportPrescription/reportPrescription";
import RerunConfirmModal from "../../../components/modals/rerunConfirmModal/rerunConfirmModal";
import SendToPlanConfirmModal from "../../../components/modals/sendToPlanConfirmModal/sendToPlanConfirmModal";
import { CmmOrdersTableDataModel } from "../../../data-model/tablesData";
import { ModalId } from "../../../enums/modalId";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import {
  closeModal,
  setOpenedModalId,
} from "../../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { handleButtonWithThreeDotsClick } from "../actionHandlers/buttonWiththreDotsActionHandlers";
import { useCmmOrderData } from "../hooks/useCmmOrderData";
import { useCmmOrderFilter } from "../hooks/useCmmOrderFilter";
import { useCmmOrderModals } from "../hooks/useCmmOrderModals";
import { useCmmOrderSearch } from "../hooks/useCmmOrderSearch";
import { CmmOrderTableHeader } from "../table/cmmOrderTableData";
import { processFilterData } from "../utils/filterDataProcessor";
import { getLoggedInUserDisplayNameForNotes } from "../utils/getLoggedInUserDisplayNameForNotes";
import {
  handleEndIndexOfTable,
  handleRowExpandChangeFromTableBody,
  navigateToForm,
} from "../utils/tableHandlers";
import CmmOrderNotesSidebar from "./cmmOrderNotesSidebar";

const CmmOrderTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();
  const tableAttributes = getTableDataFromContext("allCmmOrders");
  const { openedModalId, metaData } = useSelector(
    (state: RootState) => state.modalSliceNew,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const initialLimit = 120;
  const [endIndex, setEndIndex] = useState<number>(0);
  const [notesSidebarOpen, setNotesSidebarOpen] = useState(false);
  const [notesSidebarRow, setNotesSidebarRow] =
    useState<CmmOrdersTableDataModel | null>(null);

  const handleNotesIconClick = useCallback((row: CmmOrdersTableDataModel) => {
    setNotesSidebarRow(row);
    setNotesSidebarOpen(true);
  }, []);

  const handleCloseNotesSidebar = useCallback(() => {
    setNotesSidebarOpen(false);
  }, []);

  // Fetch drug cohort data
  const { drugCohort } = useDrugCohort();
  const { getDrugColor } = useDrugColorMatcher(drugCohort);

  const finalLimitToUse = useMemo(() => {
    const dataFromContext = tableAttributes.lastBatchFetched
      ? tableAttributes.lastBatchFetched
      : 0;
    const limitToFetch =
      dataFromContext === 0
        ? initialLimit
        : (dataFromContext + 1) * initialLimit;
    return limitToFetch;
  }, []);

  const {
    ordersData,
    loading,
    pageData,
    showInLineLoader,
    filters,
    uniqueDrugnames,
    uniqueFormNames,
    user,
    allCmmOrdersTableData,
    tableDataForTable,
    setTableDataForTable,
  } = useCmmOrderData(getDrugColor);

  const loggedInUserNameForNotes = useMemo(
    () =>
      getLoggedInUserDisplayNameForNotes(
        providerDetails,
        user?.email,
        user?.id,
      ),
    [providerDetails, user?.email, user?.id],
  );

  // First get valid filters
  const validFilters = useMemo(() => {
    return filters.filter((filter) => filter.name === "allCmmOrders");
  }, [filters]);

  const handleFetchDrugUsage = () => {
    dispatch(setOpenedModalId({ id: ModalId.DRUG_LABEL_MODAL }));
  };

  const {
    searchText,
    initialSearch,
    setBatchNumberRetreived,
    filterState,
    setFilterState,
    search,
    fetchAllOrders,
    resetAndFetchOrders,
    handleSearch,
  } = useCmmOrderSearch(finalLimitToUse, validFilters, processFilterData);

  const { filterData, setFilterForTable } = useCmmOrderFilter(
    filters,
    uniqueDrugnames,
    uniqueFormNames,
    finalLimitToUse,
    processFilterData,
    initialSearch,
    fetchAllOrders,
    filterState,
    setFilterState,
    endIndex,
    allCmmOrdersTableData,
  );

  const {
    openDeleteModal,
    setOpenDeleteModal,
    data,
    setData,
    openSendToPlanModal,
    sendToPlanModalType,
    openRerunModal,
    rerunType,
    rerunOrderData,
    handleSendToPlanModalOpen,
    handleSendToPlanConfirm,
    handleRerunModalOpen,
    handleRerunConfirm,
    onCmmOrderDeleteSuccess,
    closeDeleteModal,
    closeSendToPlanModal,
    closeRerunModal,
    openReportPrescriptionModal,
    selectedOrderIdForReportPrescription,
    handleReportPrescriptionModalOpen,
    closeReportPrescriptionModal,
    openReportMedicationModal,
    selectedOrderIdForReportMedication,
    handleReportMedicationModalOpen,
    closeReportMedicationModal,
  } = useCmmOrderModals(
    ordersData,
    user?.email ?? "",
    resetAndFetchOrders,
    searchText,
    search,
    fetchAllOrders,
  );

  const handleCloseCmmInputModal = () => {
    dispatch(closeModal());
  };

  return (
    <>
      <CustomTable
        tableHeaders={CmmOrderTableHeader}
        tableData={tableDataForTable}
        itemsPerPage={10}
        pagesPerView={4}
        count={filterState ? allCmmOrdersTableData?.length : ordersData?.length}
        totalCount={
          filterState
            ? allCmmOrdersTableData?.length
            : pageData?.[PaginationKeys.TotalCount]
        }
        isFetching={loading}
        tableName={"allCmmOrders"}
        searchingText={handleSearch}
        placeholder="Search by Patient Name, Member ID, CMM Key, Sequence ..."
        endIndexOfTable={(index) =>
          handleEndIndexOfTable(
            index,
            ordersData,
            pageData,
            finalLimitToUse,
            setEndIndex,
            setBatchNumberRetreived,
            setTableDataForContext,
          )
        }
        showOtherButton={true}
        onReviewButtonClick={(data) => navigateToForm(data, navigate)}
        onRefreshButton={resetAndFetchOrders}
        onRowClick={(data) => navigateToForm(data, navigate)}
        onNotesIconClick={handleNotesIconClick}
        isDisabled={loading}
        onDeleteIconClick={(data) => {
          setOpenDeleteModal(true);
          setData(data);
        }}
        showFilterButton={!loading && searchText.length <= 3}
        filterSectionsData={filterData}
        onFilterApplyClicked={(data) => {
          setFilterForTable(data);
          dispatch(setFilter(data));
        }}
        otherButtonMeta={[
          ...(buildFileUploadButtonMeta(
            () =>
              dispatch(
                setOpenedModalId({ id: ModalId.STAT_FILE_UPLOAD_MODAL }),
              ),
            loading,
            getOrgIdForFetchExternalWorklist() || "",
          ) ?? []),
          {
            text: "Fetch Drug Usage",
            onClick: handleFetchDrugUsage,
            disabled: false,
            buttonType: "secondary",
            size: "medium",
          },
        ]}
        showInLineLoader={showInLineLoader}
        showRefreshButton={true}
        expandableRowContent={(rowData, rowIndex) => (
          <CmmOrderExpandableRow rowData={rowData} />
        )}
        onRowExpansionChangeFromTableBody={(expanded, id) =>
          handleRowExpandChangeFromTableBody(
            expanded,
            id,
            tableDataForTable,
            setTableDataForTable,
          )
        }
        isExpandableRowTable={true}
        onButtonWithThreeDotsOptionClick={(optionId, rowId) =>
          handleButtonWithThreeDotsClick(
            optionId,
            rowId,
            user?.email ?? "",
            resetAndFetchOrders,
            handleSendToPlanModalOpen,
            handleRerunModalOpen,
            navigate,
            handleReportPrescriptionModalOpen,
            handleReportMedicationModalOpen,
          )
        }
      />
      {openedModalId === ModalId.STAT_FILE_UPLOAD_MODAL && (
        <StatFileUploadModal onClose={() => dispatch(closeModal())} />
      )}
      {openDeleteModal && (
        <CmmOrderDeleteModal
          onClose={closeDeleteModal}
          data={data}
          onDeleteSuccess={onCmmOrderDeleteSuccess}
        />
      )}
      {openSendToPlanModal && (
        <SendToPlanConfirmModal
          onClose={closeSendToPlanModal}
          onSaveClick={handleSendToPlanConfirm}
          modalType={sendToPlanModalType}
        />
      )}
      {openRerunModal && rerunType && (
        <RerunConfirmModal
          onClose={closeRerunModal}
          onSaveClick={handleRerunConfirm}
          rerunType={rerunType}
          orderData={rerunOrderData}
        />
      )}
      {openedModalId === ModalId.CMM_INPUT_VIEWER_MODAL && (
        <CmmInputViewerModalForCmmOrders
          onClose={handleCloseCmmInputModal}
          orderId={metaData?.orderId ?? ""}
        />
      )}
      {openReportPrescriptionModal && (
        <ReportPrescriptionModal
          onClose={closeReportPrescriptionModal}
          orderId={selectedOrderIdForReportPrescription ?? ""}
        />
      )}
      {openReportMedicationModal && (
        <ReportMedicationModal
          onClose={closeReportMedicationModal}
          orderId={selectedOrderIdForReportMedication ?? ""}
        />
      )}
      {openedModalId === ModalId.DRUG_LABEL_MODAL && (
        <DrugLabelModal onClose={handleCloseCmmInputModal} />
      )}
      <CmmOrderNotesSidebar
        isOpen={notesSidebarOpen}
        onClose={handleCloseNotesSidebar}
        row={notesSidebarRow}
        loggedInUserName={loggedInUserNameForNotes}
      />
    </>
  );
};

export default CmmOrderTable;
